import type { JSX } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import type { Theme } from '@mui/material';
import { tss } from 'tss-react/mui';
import './barchart.css';
import Text from '../Text';

export type BarChartWithIndicatorArrowProps = JSX.IntrinsicElements['div'] & {
    size: 'lg' | 'sm' | 'xs';
    value: number;
    arrowValue?: number;
    min: number;
    max: number;
    className?: string;
    primaryColor?: string;
};

export default function BaseBarChartWithIndicatorArrow(
    props: BarChartWithIndicatorArrowProps,
) {
    const {
        size = 'lg',
        value,
        arrowValue,
        min,
        max,
        className,
        primaryColor,
        ...rootProps
    } = props;
    const minimum = min >= 0 ? min : 0;
    const maximum = max <= 100 ? max : 100;
    const barWidth = value > minimum && value <= maximum ? value : '0';
    const arrowPosition =
        value >= minimum && value <= maximum ? arrowValue : null;
    const smUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'));
    const barHeight = {
        lg: smUp ? 16 : 10,
        sm: 8,
        xs: 4,
    }[size];
    const { classes, cx } = useBarChartWithArrowIndicatorStyles();
    const theme = useTheme();

    return (
        <div className={cx(className, classes.root)} {...rootProps}>
            {arrowPosition == null ? null : (
                <span
                    className={classes.arrow}
                    style={{
                        left: `${arrowPosition}%`,
                        bottom: barHeight,
                    }}
                >
                    <Text
                        className={cx(classes.arrow__label)}
                        size="xs"
                        fontWeight="600"
                        color="label"
                    >
                        BP
                    </Text>
                    <svg
                        className={classes.arrow__triangle}
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M10 0 L-40 100 L20 17 Z" />
                    </svg>
                </span>
            )}
            <svg
                className={classes.bar}
                height={barHeight}
                fill="transparent"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
            >
                <rect className={classes.bar__background} />
                <rect
                    className={classes.bar__bar}
                    fill={
                        !primaryColor ? theme.bino.primaryColor : primaryColor
                    }
                    width={barWidth}
                    height="100"
                />
                {value}
            </svg>
        </div>
    );
}

const useBarChartWithArrowIndicatorStyles = tss.create(({ theme }) => ({
    root: {
        position: 'relative',
        lineHeight: 0,
    },
    bar: {
        // transformOrigin: '50% 50% 0px !important',
        width: '100%',
        borderRadius: 5,
        //backgroundColor: '#efefef',
    },
    bar__background: {
        fill: theme.palette.mode === 'dark' ? `rgba(0,0,0,0.1)` : '#efefef',
        width: 100,
        height: 100,
    },
    bar__bar: {
        transition: 'width 0.3s!important',
        animation: 'progress-bar-appear 0.5s forwards',
    },

    arrow: {
        position: 'absolute',
        display: 'flex',
        flexDirection: 'column',
    },
    arrow__triangle: {
        textAlign: 'center',
        transform: 'rotate(-180deg)',
        position: 'relative',
        float: 'left',
        animation: 'triangle-appear 1.5s forwards',
        height: 6,
        width: 20,
        marginLeft: -10,
        fill: '#aaaaaa',
        [theme.breakpoints.up('sm')]: {
            height: 8,
        },
    },
    arrow__label: {
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
        width: 20,
        textAlign: 'center',
        top: -13,
        left: -9.5,
    },
}));
